import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';

import { GetMetadataActivity } from 'app/model/pipeline/get-metadata.activity';
import { Dataset } from 'app/model/dataset/dataset';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { convertParametersToModel, fillParameterValuesForResource } from 'app/shared/utils/parameter-converter.util';
import { Owned } from 'app/model/base/named';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import {
    convertStoreSettingsToResource,
    convertStoreSettingsToModel
} from './copysource/store-read-settings/store-read-settings.util';
import {
    convertFormatSettingsToResource,
    convertFormatSettingsToModel
} from './copysource/format-read-settings/format-read-settings.util';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { ActivityConnectionContext, IGetMetadataActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import {
    findLinkedServiceFromContext,
    setDbFromLs,
    setGetMetadataDataset
} from 'app/shared/utils/update-pipeline-skill.util';
import { createDatasetWithLinkedService } from 'app/authoring/component/activities/activity-inline-dataset/utils';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { ModelService } from 'app/model/services/model.service';
import { IFrameService } from 'app/services/iframe.service';
import { getLinkedServiceTypeFromDatasetMetadataAndConnectionKind } from 'app/shared/utils/linkedservice-type.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(GetMetadataActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            fieldList: '*',
            storeSettings: '*',
            formatSettings: '*',
            dataset: '*',
            datasetSettings: '*'
        }
    }
})
export class GetMetadataActivityAdapter extends ActivityAsyncAdapter<
    GetMetadataActivity,
    Model.GetMetadataActivity,
    IGetMetadataActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(GetMetadataActivity);
    }

    public override async postToModel(activity: GetMetadataActivity): Promise<void> {
        await super.postToModel(activity);
        if (__IS_FABRIC_DPE__ && activity.dataset) {
            const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
                this.context.contextData,
                'DatasetRegistryService'
            );
            const iframeService = await getServiceInstance<IFrameService>(
                this.context.contextData,
                'IFrameService'
            );
            activity.dataset.customLinkedServiceType = getLinkedServiceTypeFromDatasetMetadataAndConnectionKind(
                activity.dataset,
                activity.dataset.linkedService?.tridentConnectionKind,
                {
                    iframeService,
                    datasetRegistry
                }
            );
        }
    }

    public override async toFabricCopilotContext(activity: GetMetadataActivity): Promise<IGetMetadataActivityContext> {
        if (activity) {
            const activityContext: IGetMetadataActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.dataset) {
                activityContext.Connection = await this.getDatasetCopilotContext(activity.dataset);
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IGetMetadataActivityContext,
        model?: GetMetadataActivity
    ): Promise<GetMetadataActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                const linkedServiceInfo = await findLinkedServiceFromContext(context.Connection, {
                    modelService,
                    iframeService
                });
                if (linkedServiceInfo) {
                    const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
                        this.context.contextData,
                        'DatasetRegistryService'
                    );
                    const linkedServiceRegistry = await getServiceInstance<LinkedServiceRegistryService>(
                        this.context.contextData,
                        'LinkedServiceRegistryService'
                    );
                    const { dataset } = createDatasetWithLinkedService(
                        linkedServiceInfo.linkedService,
                        linkedServiceInfo.linkedServiceType,
                        model,
                        { linkedServiceRegistry, datasetRegistry },
                        {
                            rootFolder: context.Connection.Type === 'LakehouseFiles' ? 'Files' : 'Tables'
                        }
                    );
                    if (dataset) {
                        dataset.customLinkedServiceType = linkedServiceInfo.linkedServiceType;

                        setGetMetadataDataset(model, dataset);
                        const sourceDatasetAdapter = this.context.asyncAdapterFor<
                            Dataset,
                            Model.Dataset,
                            ActivityConnectionContext
                        >(Metamodel.modelOf(dataset));
                        model.dataset = await sourceDatasetAdapter.fromFabricCopilotContext(
                            context.Connection,
                            model.dataset
                        );
                        // post update the database from linked service
                        setDbFromLs(model.dataset, model.dataset.linkedService);
                    }
                }
            }
            if (!model.fieldList) {
                model.fieldList = [];
            }
        }
        return model;
    }

    protected async _toResource(activity: GetMetadataActivity): Promise<Model.GetMetadataActivity> {
        const modelGetMetadataActivity: Model.GetMetadataActivity = super._getCommonActivityProperties(
            activity,
            'GetMetadata'
        ) as Model.GetMetadataActivity;
        modelGetMetadataActivity.typeProperties = modelGetMetadataActivity.typeProperties || { dataset: undefined };

        if (activity.fieldList && Array.isArray(activity.fieldList)) {
            modelGetMetadataActivity.typeProperties.fieldList = activity.fieldList
                .filter(
                    (field) => field && !(field.value == null && ParameterConfig.VALUE.templateValue(field) == null)
                )
                .map((argument: ParameterConfig) => {
                    return ValueAdapter.toResource(argument, ParameterConfig.VALUE);
                });
        }

        if (activity.dataset) {
            if (this.context.adapterConfig.inlineDataset || this.context.adapterConfig.convertToFabric) {
                const datasetModel = Metamodel.modelOf(activity.dataset);
                const datasetAdapter = this.context.asyncAdapterFor(datasetModel);
                if (datasetAdapter) {
                    modelGetMetadataActivity.typeProperties.datasetSettings = (
                        await datasetAdapter.toResource(activity.dataset)
                    ).properties;
                } else {
                    this._logError('Cannot find dataset adapter in GetMetadataActivityAdapter', {
                        datasetName: activity.dataset.name,
                        datasetType: datasetModel?.name,
                    });
                }
            } else {
                modelGetMetadataActivity.typeProperties.dataset = this.getDatasetReference(activity);
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelGetMetadataActivity, parsed);
        }
        await convertStoreSettingsToResource(
            activity.storeSettings,
            modelGetMetadataActivity.typeProperties,
            this.context
        );
        await convertFormatSettingsToResource(
            activity.formatSettings,
            modelGetMetadataActivity.typeProperties,
            this.context
        );

        return modelGetMetadataActivity;
    }

    protected async _toModel(modelActivity: Model.GetMetadataActivity): Promise<GetMetadataActivity> {
        const activity: GetMetadataActivity = GetMetadataActivity.MODEL.create<GetMetadataActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { dataset: undefined };

        const result: ParameterConfig[] = [];
        if (modelActivity.typeProperties.fieldList && modelActivity.typeProperties.fieldList.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            modelActivity.typeProperties.fieldList.forEach((argument: any) => {
                const paramConfig = Owned.create<ParameterConfig>(ParameterConfig.MODEL, activity);
                ValueAdapter.toModel(paramConfig, ParameterConfig.VALUE, argument);
                result.push(paramConfig);
            });
        }
        activity.fieldList = result;

        if (modelActivity.typeProperties.dataset) {
            activity.dataset = ReferenceAdapter.toProxyModel<Dataset>(
                Metamodel.modelFor(Dataset),
                modelActivity.typeProperties.dataset,
                this.context
            );
            if (modelActivity.typeProperties.dataset.parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.dataset,
                    activity.datasetParameters,
                    modelActivity.typeProperties.dataset.parameters
                );
            }
        } else if (modelActivity.typeProperties.datasetSettings) {
            const datasetSettings = modelActivity.typeProperties.datasetSettings;
            const datasetModel = Metamodel.modelByName(datasetSettings.type);
            const datasetAdapter = this.context.asyncAdapterFor(datasetModel);
            const dataset = Owned.create<Dataset>(datasetModel, activity);
            await datasetAdapter.toModel(
                { name: generateUUID().replace(/-/g, '_'), properties: datasetSettings },
                dataset
            );
            activity.dataset = dataset;

            if (this._shouldGetArtifactDMTSConnectionFromSolutionTemplate()) {
                const dmtsCredentialKey = `${modelActivity.name}/GetMetadata`;
                const dmtsCredentialForArtifact = this.context.adapterConfig.artifactDMTSCredentials.get(dmtsCredentialKey);
                if (dmtsCredentialForArtifact != null) {
                    activity.dataset.dmtsCredentialForArtifact = dmtsCredentialForArtifact;
                }
            }
        }

        await convertStoreSettingsToModel(modelActivity.typeProperties.storeSettings, activity, this.context);
        await convertFormatSettingsToModel(modelActivity.typeProperties.formatSettings, activity, this.context);

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelActivity, GetMetadataActivity.MODEL)
        );
        return activity;
    }

    private getDatasetReference(activity: GetMetadataActivity): Model.DatasetReference {
        const datasetReference = ReferenceAdapter.toResource(
            activity.dataset as Dataset,
            this.context
        ) as Model.DatasetReference;

        datasetReference.parameters = fillParameterValuesForResource(
            activity.datasetParameters,
            this.context,
            this.ensureLogger()
        );
        return datasetReference;
    }
}
